import { DateTime } from 'luxon';

import {
    ICreateOfferEventInput,
    ICreateOfferInput,
    IFileFragment,
    IOfferEventDateInput,
    IUserListItemFragment,
    ITrainingListItemFragment,
    IExternalContentListItemFragment,
    ICourseListItemFragment,
    Maybe,
    IUpdateOfferInput,
    IOfferEventType,
    IFlatOfferEventDateFragment,
    ICertificateWithScoringFragment,
    IOfferEventEnrollmentManageableFragment,
    IOfferEventEnrollmentFragment,
    IOfferEventDetailFragment,
    IScormListItemFragment,
    IVideoListItemFragment,
    IBasicFileFragment,
    IExpiryScale,
} from 'graphql/types';
import { IGroupConditionInput } from 'user/types';
import {
    TExtraCategoryInput,
    TExtraCategoriesFormInput,
} from 'organisation/types';
import {
    TDateFilterValue,
    TObjectFilterValue,
    TRangeFilterValue,
    TStringFilterValue,
    TFileFormValue,
} from 'common/types';

export type TCertificateFormValue = {
    __typename: 'Certificate';
    id: string;
    name: string;
    expiry?: number | null;
    expiryScale?: IExpiryScale | null;
    scoring: Array<{
        __typename: 'Scoring';
        score: number;
        scoringSystem: {
            __typename: 'ScoringSystem';
            id: string;
            nameSingular: string;
            namePlural: string;
        };
    }>;
};

export type TCreateOfferFormValues = {
    image?: TFileFormValue | null;
    title: string;
    description?: string;
    titleColor?: string | null;
    overviewImage?: TFileFormValue | null;
    hasDetailPage?: boolean;
};

export type TOfferFormValues = {
    offer: ICreateOfferInput & TExtraCategoriesFormInput;
    event: ICreateOfferEventInput & {
        offerEventTitle?: string;
        certificate?: ICertificateWithScoringFragment;
    };
};

export type TWebinarFormValues = {
    isServiceProviderWebinar?: boolean;
    dates?: IOfferEventDateInput[];
    closingDate?: string | DateTime;
};

export type TMeetingFormValues = {
    dates?: IOfferEventDateInput[];
    openingDate?: string | DateTime;
    closingDate?: string | DateTime;
    unenrollClosingDate?: string | DateTime;
    publicParticipantList?: boolean;
};

export type TTrainingFormValues = {
    accessTrainings: ITrainingListItemFragment[];
    startDate?: string | DateTime;
    endDate?: string | DateTime;
    closingDate?: string | DateTime;
};

export type TOfferEventValues = {
    offerEventTitle?: string;
    certificate?: ICertificateWithScoringFragment;
    trainers: IUserListItemFragment[];
    trainerApproval: boolean;
    minSubscriptions?: number;
    maxSubscriptions?: number;
    waitingList?: boolean;
    price?: number;
    descriptionBeforeSubscription?: string;
    descriptionAfterSubscription?: string;
    accessTrainings: ITrainingListItemFragment[];
    accessExternalContents?: IExternalContentListItemFragment[];
    accessCourses?: ICourseListItemFragment[];
    showParticipantList: boolean;
    groupConditions?: IGroupConditionInput[];
    type: IOfferEventType;
    accessScorms?: IScormListItemFragment[];
    accessVideos?: IVideoListItemFragment[];
    scoreThreshold?: number;
};

export type TOldOfferEventFormValues = {
    publish?: boolean;
} & TOfferEventValues &
    Maybe<TCreateOfferFormValues> &
    Maybe<TExtraCategoriesFormInput> &
    Maybe<TWebinarFormValues> &
    Maybe<TMeetingFormValues> &
    Maybe<TTrainingFormValues>;

export type TUpdateOfferFormInput = IUpdateOfferInput & {
    image?: IFileFragment | IBasicFileFragment | null;
    overviewImage?: IFileFragment | IBasicFileFragment | null;
    extraCategories: TExtraCategoryInput;
};

export type TOfferPermissions = {
    canUpdate: boolean;
    canDelete: boolean;
};

export type TOfferEventPermissions = {
    canManageSubscriptions: boolean;
};

export type TOfferEventDateFormInput = Omit<
    IOfferEventDateInput,
    'locationId' | 'locationString' | 'trainers'
> & {
    location?: {
        id?: string;
        title?: string;
    } | null;
    locationId?: string | null;
    locationString?: string | null;
    trainers?: string[] | null;
};

export enum EOfferEventCategoryType {
    Meeting = 'MEETING',
    Offer = 'OFFER',
}

export type TOfferEventDetailEnrollment =
    | IOfferEventEnrollmentManageableFragment
    | IOfferEventEnrollmentFragment;

export type TOfferEventEnrollmentGroup =
    IOfferEventDetailFragment['groupEnrollmentCounts'][number]['group'];

export type TOfferEventFormValues = {
    title?: string; // Can be optional when offerEventTitle is provided
    offerEventTitle?: string;
    description?: string;
    image?: TFileFormValue | null;
    overviewImage?: TFileFormValue | null;
    titleColor?: string | null;
    hasDetailPage?: boolean;
    publish?: boolean;
    certificate?: TCertificateFormValue | null;
    trainers?: string[];
    trainerApproval?: boolean;
    minSubscriptions?: number;
    maxSubscriptions?: number;
    waitingList?: boolean;
    price?: number;
    descriptionBeforeSubscription?: string;
    descriptionAfterSubscription?: string;
    accessTrainings?: string[];
    accessExternalContents?: string[];
    accessCourses?: string[];
    showParticipantList?: boolean;
    groupConditions?: IGroupConditionInput[];
    type: IOfferEventType;
    accessScorms?: string[];
    accessVideos?: string[];
    scoreThreshold?: number;
    groupManagersCanEnrollParticipants?: boolean;
    extraCategories?: TExtraCategoryInput; // Handled by the extra category values mutation
    isServiceProviderWebinar?: boolean;
    dates?: TOfferEventDateFormInput[];
    openingDate?: DateTime | null;
    closingDate?: DateTime | null;
    unenrollClosingDate?: DateTime | null;
    publicParticipantList?: boolean;
};

export interface IOfferEventEnrollmentManagementFilterValues {
    dateFrom?: TDateFilterValue;
    dateTo?: TDateFilterValue;
    extraFields?: TStringFilterValue;
    presenceFrom?: TRangeFilterValue;
    presenceTo?: TRangeFilterValue;
    groupIds?: TObjectFilterValue[];
    attendance?: TRangeFilterValue[];
}

export interface IGroupConditionRow {
    canEnrollParticipants: boolean;
    canSubscribe: boolean;
    count?: number;
    group: { id: string; name: string };
    max?: number;
    min?: number;
}

export interface IOfferEventBasicListItem {
    offerEventTitle?: string | null;
    type?: IOfferEventType | null;
    firstDate?: IFlatOfferEventDateFragment | null;
    offer?: { title: string } | null;
    image?: { url?: string | null } | null;
}
